import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { useStowageUIStore } from 'src/context/StowageUIContext';
import { TITLE_MAX_LENGTH, cleanStowagePlanData, createEmpyStowagePlan } from 'src/factory/Stowage/StowagePlanFactory';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { useEffect, useState } from 'react';
import OdfjellInputComponent from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { getMasterStowagePlan } from 'src/utils/stowage/stowagePlanUtils';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { StowagePlanShareTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanShareTypeEnum';
import { InputContainer } from './PageHeaderStowagePublishPromotePanel.style';
import { ButtonActionType } from '../pageHeaderStowagePublishPromoteButton/PageHeaderStowagePublishPromoteButton';
import { useAuthStore } from 'src/context/AuthContext';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import useGetStowagePlan from 'src/api/stowage/useGetStowageApi';
import { useStowageUrlChangeHandler } from 'src/hooks/stowage/urlHandler/useStowageUrlChangeHandler';

type Props = {
    type: ButtonActionType;
};

const PageHeaderStowagePublishPromotePanel: React.FC<Props> = (props: Props) => {
    const { activeStowagePlan, stowagePlans, setActiveStowagePlan } = useStowagePlanStore();
    const { setIsPublishPromoteOpen } = useStowageUIStore();
    const { updateStowagePlan, addStowagePlan } = usePostStowageApi();
    const { loggedInUser } = useAuthStore();
    const { setQueryParamStowagePlan } = useStowageQueryParamsUtils();
    const { getStowagePlanChanges } = useGetStowagePlan();
    const { setStowageDataInStowagePlan } = useStowageUrlChangeHandler();
    const [titleInput, setTitleInput] = useState<string>('');
    const [commentsInput, setCommentsInput] = useState<string>('');

    useEffect(() => {
        if (!activeStowagePlan) {
            return;
        }

        let newTitle = activeStowagePlan.title;
        if (newTitle.length > TITLE_MAX_LENGTH) {
            newTitle = newTitle.slice(0, TITLE_MAX_LENGTH);
        }

        setTitleInput(newTitle);
        setCommentsInput(activeStowagePlan.comments || '');
    }, [activeStowagePlan]);

    const onTitleChange = (newTitle: string) => {
        setTitleInput(newTitle);
    };

    const onCommentsChange = (newComments: string) => {
        setCommentsInput(newComments);
    };

    const onPublishStowagePlan = async () => {
        const _stowagePlan = {
            ...activeStowagePlan,
            type: StowagePlanTypeEnum.Published,
            shareType: StowagePlanShareTypeEnum.Shared,
            title: titleInput,
            comments: commentsInput,
        };
        updateStowagePlan(_stowagePlan);
    };

    const onPromoteStowagePlan = async () => {
        if (!activeStowagePlan) return;

        const previousMasterPlan = getMasterStowagePlan(stowagePlans);
        const newMasterPlanVersion = (previousMasterPlan?.masterPlanVersion || 0) + 1;

        let newPlan = createEmpyStowagePlan(
            activeStowagePlan.imoNumber,
            loggedInUser?.fullName || loggedInUser?.userName || activeStowagePlan.owner,
            activeStowagePlan.stowagePlanGuid,
            titleInput,
            newMasterPlanVersion
        );

        newPlan = {
            ...newPlan,
            type: StowagePlanTypeEnum.Master,
            shareType: StowagePlanShareTypeEnum.Shared,
            comments: commentsInput,
        };

        const { updatedStowagePlan, updatedStowagePlans } = await addStowagePlan(newPlan);
        if (updatedStowagePlan) {
            // Fetch the changes for the new Master plan (includes parent chain changes)
            const stowagePlanChanges = await getStowagePlanChanges(updatedStowagePlan.stowagePlanGuid, true);

            // Apply the changes directly with the updated stowage plans list
            const cleanedPlan = cleanStowagePlanData(updatedStowagePlan);
            const { updatedStowagePlan: planWithData } = await setStowageDataInStowagePlan(stowagePlanChanges, cleanedPlan, updatedStowagePlans);

            if (planWithData) {
                setActiveStowagePlan({ ...planWithData });
            }

            // Update URL to reflect the new Master plan
            setQueryParamStowagePlan(updatedStowagePlan.stowagePlanGuid, ['portCallGuid', 'berthCallGuid', 'cargoOperationGuid']);
        }
    };

    const onPublishPromoteStowagePlan = async () => {
        if (props.type === 'publish') {
            await onPublishStowagePlan();
        } else if (props.type === 'promote') {
            await onPromoteStowagePlan();
        }
        onClose();
    };

    const onClose = () => {
        setIsPublishPromoteOpen(false);
    };

    return (
        <OdfjellPanel
            headerTitle={(props.type === 'promote' ? 'Promote' : 'Publish') + ' stowage plan'}
            defaultPosition="center"
            panelWidth={PANEL_CONSTANTS.MEDIUM_WIDTH}
            hasCurtain={true}
            preventOutsideClose={true}
            onCancel={onClose}
            onAction={onPublishPromoteStowagePlan}
            showCancelButton={true}
        >
            <InputContainer>
                <OdfjellInputComponent
                    description="Title"
                    type="text"
                    isReadOnly={false}
                    value={titleInput}
                    onChange={(e) => onTitleChange(e.event.target.value)}
                    maxTextLength={50}
                />
            </InputContainer>
            <InputContainer>
                <OdfjellInputComponent
                    description="Comments"
                    type="textarea"
                    isReadOnly={false}
                    value={commentsInput}
                    onChange={(e) => onCommentsChange(e.event.target.value)}
                    maxTextLength={500}
                />
            </InputContainer>
        </OdfjellPanel>
    );
};

export default PageHeaderStowagePublishPromotePanel;
